import './style.css';
import { DIFFICULTIES, difficultyById } from './ai/difficulty';
import { PERSONALITIES, personalityById } from './ai/personality';
import { GameLoop } from './core/loop';
import { clamp01 } from './core/math';
import { loadCalibration, saveCalibration } from './pose/calibration';
import { PoseSource } from './pose/source';
import { PoseProcessor } from './pose/processor';
import type { PoseFrame } from './pose/types';
import * as progress from './progress';
import { Renderer } from './render/renderer';
import { Tuner } from './dev/tuner';
import { loadTuning, saveTuning } from './recog/tuning';
import {
  applyFits,
  fitThresholds,
  MovementProfiler,
  type Fit,
  type MoveKind,
} from './recog/profiler';
import { Scorer } from './score/scoring';
import { Match, type Phase } from './sim/match';

/* ------------------------------------------------------------------ helpers */

const $ = <T extends HTMLElement = HTMLElement>(id: string): T => {
  const el = document.getElementById(id);
  if (!el) throw new Error(`missing #${id}`);
  return el as T;
};

const screens = {
  menu: $('screen-menu'),
  calibrate: $('screen-calibrate'),
  warmup: $('screen-warmup'),
  ready: $('screen-ready'),
  round: $('screen-round'),
  final: $('screen-final'),
  tuner: $('screen-tuner'),
};
type ScreenName = keyof typeof screens;

function show(name: ScreenName | null): void {
  for (const [k, el] of Object.entries(screens)) el.hidden = k !== name;
  $('hud').hidden = name !== null;
}

const fmtTime = (ms: number) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/* -------------------------------------------------------------------- state */

const video = $<HTMLVideoElement>('cam');
const canvas = $<HTMLCanvasElement>('stage');

const processor = new PoseProcessor();
const match = new Match();
const renderer = new Renderer(canvas, video);
let pose: PoseSource | null = null;
let latestFrame: PoseFrame | null = null;
let store = progress.load();

// Any per-device threshold overrides must apply to the GAME, not just the tuning
// screen — otherwise a player tunes it until their movements register, goes back
// to sparring, and finds nothing changed.
loadTuning();

let selectedPersonality = PERSONALITIES[0].id;
let selectedDifficulty = 'medium';
let mode: 'menu' | 'calibrate' | 'warmup' | 'ready' | 'fight' | 'round' | 'final' | 'tuner' = 'menu';
let tuner: Tuner | null = null;
let sessionStart = 0;

/* --------------------------------------------------------------- menu setup */

function buildChips(): void {
  const pHost = $('pick-personality');
  pHost.innerHTML = '';
  for (const p of PERSONALITIES) {
    const b = document.createElement('button');
    b.className = 'chip';
    b.textContent = p.name;
    b.setAttribute('aria-pressed', String(p.id === selectedPersonality));
    b.onclick = () => {
      selectedPersonality = p.id;
      buildChips();
      $('personality-blurb').textContent = `${p.blurb}  ·  Watch for: ${p.tell}`;
    };
    pHost.appendChild(b);
  }
  const active = personalityById(selectedPersonality);
  $('personality-blurb').textContent = `${active.blurb}  ·  Watch for: ${active.tell}`;

  const dHost = $('pick-difficulty');
  dHost.innerHTML = '';
  for (const d of DIFFICULTIES) {
    const b = document.createElement('button');
    b.className = 'chip';
    b.textContent = `${d.name} · ${d.reactionMs} ms`;
    b.setAttribute('aria-pressed', String(d.id === selectedDifficulty));
    b.onclick = () => {
      selectedDifficulty = d.id;
      buildChips();
    };
    dHost.appendChild(b);
  }
}
buildChips();

if (store.sessions.length) {
  $('menu-status').textContent = `${store.sessions.length} session${store.sessions.length > 1 ? 's' : ''} · ${store.streak}-day streak · best ${store.best.overall}`;
} else {
  // First session defaults to one 90-second round, not 3x2min.
  //
  // The most common way a fitness app loses someone is a first session so long
  // they are too sore to come back, and the competitive read supports going even
  // shorter than instinct suggests — Shadowbox runs 20-second rounds. Six
  // minutes of continuous punching is a lot for someone who has never done it.
  // Experienced users still get the full defaults, because the inputs persist.
  $<HTMLInputElement>('opt-rounds').value = '1';
  $<HTMLInputElement>('opt-seconds').value = '90';
  $('menu-status').textContent = 'First session: one 90-second round. You can make it longer once you know what it feels like.';
}

/* ----------------------------------------------------------- pose ingestion */

type RawPose = Parameters<NonNullable<PoseSource['onPose']>>[0];
let latestRaw: RawPose | null = null;

function onRawPose(raw: RawPose): void {
  latestRaw = raw;
  if (mode === 'calibrate') {
    const pct = processor.calibrate(raw);
    $('calib-arc').setAttribute('stroke-dashoffset', String(327 * (1 - pct)));
    $('calib-pct').textContent = `${Math.round(pct * 100)}%`;
    $('calib-hint').textContent =
      raw.quality < 0.6 ? 'Step back — I need your hips and head in frame' : 'Hold that guard…';
    if (pct >= 1) finishCalibration();
    return;
  }
  const f = processor.process(raw);
  latestFrame = f;
  if (mode === 'warmup') {
    onWarmupFrame(f);
    return;
  }
  if (mode === 'tuner') {
    tuner?.onFrame(f, raw);
    return;
  }
  match.onPoseFrame(f);
}

/**
 * Calibration watchdog.
 *
 * A stuck calibration screen has four independent causes — no camera frames, no
 * inference, no person found, or a person found but judged too low-quality — and
 * from the outside they are indistinguishable. This names which one it is,
 * in the UI, with the actual counters, and always offers a way forward.
 */
let calibStart = 0;
function tickCalibrationWatchdog(now: number): void {
  if (mode !== 'calibrate' || !pose) return;
  const elapsed = now - calibStart;
  if (elapsed < 2500) return;

  const d = pose.diag;
  const diagEl = $('calib-diag');
  let problem = '';

  if (d.frames === 0) {
    problem = 'No frames are arriving from the camera. Check the camera is not in use by another app (Zoom, Teams, OBS), then reload.';
  } else if (d.detections === 0) {
    problem = 'Frames are arriving but the pose model is not running. This usually means the model failed to load.';
  } else if (d.poses === 0) {
    problem = 'The model is running but cannot find a person. Step back so your head, shoulders AND hips are all in shot, and make sure the room is lit from the front.';
  } else if (processor.collector.count === 0) {
    problem = `A person is detected, but the pose is not clean enough to measure (quality ${(d.lastQuality * 100).toFixed(0)}%, need 60%). Step back, face the camera squarely, and keep your whole torso in frame.`;
  } else {
    problem = 'Nearly there — hold still.';
  }

  diagEl.hidden = false;
  diagEl.innerHTML =
    `<b>${problem}</b>\n\n` +
    `camera frames  ${d.frames}\n` +
    `inference runs ${d.detections}\n` +
    `poses found    ${d.poses}   (empty: ${d.empty})\n` +
    `pose quality   ${(d.lastQuality * 100).toFixed(0)}%\n` +
    `samples taken  ${processor.collector.count}/45\n` +
    `visibility     ${d.visibilityUnavailable ? 'not reported by model (using geometry)' : 'reported'}` +
    (d.lastError ? `\nlast error     ${d.lastError}` : '');

  // After 8 seconds, let them through regardless. A calibration screen with no
  // exit is worse than a slightly imprecise calibration.
  if (elapsed > 8000 && d.poses > 0) $('btn-calib-force').hidden = false;
}

async function startCamera(): Promise<void> {
  const btn = $<HTMLButtonElement>('btn-start');
  btn.disabled = true;
  try {
    pose = new PoseSource({
      tier: 'lite',
      onStatus: (m) => ($('menu-status').textContent = m),
    });
    pose.onPose = onRawPose;
    pose.onLost = () => setCoach('I lost you — step back into frame');
    await pose.init(video);
    video.classList.add('live');
    pose.start();
    beginCalibration();
  } catch (err) {
    const e = err as Error;
    $('menu-status').textContent =
      e.name === 'NotAllowedError'
        ? 'Camera permission denied. Allow it in the address bar, then reload.'
        : `Could not start: ${e.message}`;
    btn.disabled = false;
  }
}

/* -------------------------------------------------------------- calibration */

function beginCalibration(): void {
  mode = 'calibrate';
  calibStart = performance.now();
  processor.collector.reset();
  processor.reset();
  pose?.resetFilters();
  show('calibrate');
  $('hud').hidden = true;
  video.classList.add('calibrating');
  $('calib-diag').hidden = true;
  $('btn-calib-force').hidden = true;
  $('calib-hint').textContent = 'Looking for you…';
  $('calib-arc').setAttribute('stroke-dashoffset', '327');
  $('calib-pct').textContent = '0%';
  const saved = loadCalibration();
  $<HTMLButtonElement>('btn-calib-skip').hidden = !saved;
}

function finishCalibration(): void {
  const c = processor.finishCalibration();
  saveCalibration(c);
  beginWarmup();
}

/* ------------------------------------------------------------------ warm-up */

/**
 * Fifteen seconds that replace the entire manual-tuning flow.
 *
 * The player throws a few punches and moves their head a few times; we measure
 * what they actually produce and set every gate from it. This exists because
 * making someone open a diagnostics screen to get their punches registering is
 * a defect, not a feature — the shipped camera-fitness products ask their users
 * to configure nothing, and the only reason we ever needed sliders is that a
 * single population default cannot fit a tall slow puncher and a short snappy
 * one at the same time. Measuring one person removes that problem entirely.
 */
const warmupPhases: Array<{ kind: MoveKind; title: string; cue: string; need: number }> = [
  { kind: 'punch', title: 'Warm-up · punches', cue: 'Throw 4 punches — any punches, at your normal speed. Not harder than usual.', need: 4 },
  { kind: 'defence', title: 'Warm-up · head movement', cue: 'Now move your head 4 times: slip left, slip right, duck, lean back.', need: 4 },
];
let warmupIndex = 0;
let profiler: MovementProfiler | null = null;
const warmupFits: Fit[] = [];

function beginWarmup(): void {
  mode = 'warmup';
  warmupIndex = 0;
  warmupFits.length = 0;
  video.classList.add('calibrating');
  show('warmup');
  $('hud').hidden = true;
  startWarmupPhase();
}

function startWarmupPhase(): void {
  const p = warmupPhases[warmupIndex];
  profiler = new MovementProfiler(p.kind);
  $('warmup-title').textContent = p.title;
  $('warmup-cue').textContent = p.cue;
  $('warmup-need').textContent = String(p.need);
  $('warmup-done').textContent = '0';
  $('warmup-pips').innerHTML = '';
  $('warmup-note').textContent = 'Move naturally. This measures you, so exaggerating now makes the game harder later.';
}

function onWarmupFrame(f: PoseFrame): void {
  const p = warmupPhases[warmupIndex];
  if (!profiler) return;
  const attempt = profiler.update(f);
  if (!attempt) return;

  const n = profiler.attempts.length;
  $('warmup-done').textContent = String(Math.min(n, p.need));
  $('warmup-pips').innerHTML = profiler.attempts
    .slice(-p.need)
    .map(() => '<span class="pip ok">✓</span>')
    .join('');

  if (n < p.need) return;

  const fits = fitThresholds(p.kind, profiler.attempts);
  warmupFits.push(...fits);
  warmupIndex++;
  if (warmupIndex < warmupPhases.length) {
    startWarmupPhase();
  } else {
    applyFits(warmupFits);
    saveTuning();
    finishWarmup();
  }
}

function finishWarmup(): void {
  profiler = null;
  video.classList.remove('calibrating');
  showReady();
}

function skipWarmup(): void {
  profiler = null;
  warmupFits.length = 0;
  video.classList.remove('calibrating');
  showReady();
}

function showReady(): void {
  mode = 'ready';
  const c = processor.calibration;
  const p = personalityById(selectedPersonality);
  const d = difficultyById(selectedDifficulty);
  $('ready-title').textContent = `${p.name} · ${d.name}`;
  $('ready-facts').innerHTML = [
    `Stance detected: <b>${c.stance}</b> (${c.stance === 'orthodox' ? 'left hand leads' : 'right hand leads'})`,
    `Reach: <b>${(c.armLength.left + c.armLength.right).toFixed(2)}</b> shoulder-widths combined`,
    `Their tell: <b>${p.tell}</b>`,
    `Their reaction: <b>${d.reactionMs} ms</b> — plus however much they read you`,
    warmupFits.length
      ? `Tuned to you: ${warmupFits.map((w) => `${w.label.toLowerCase()} <b>${w.to}</b>`).join(', ')}`
      : 'Using default detection thresholds — run the warm-up if punches are not registering.',
  ]
    .map((s) => `<li>${s}</li>`)
    .join('');
  show('ready');
  $('hud').hidden = true;
}

/* -------------------------------------------------------------------- fight */

function startFight(): void {
  mode = 'fight';
  sessionStart = performance.now();
  match.config.rounds = Number($<HTMLInputElement>('opt-rounds').value) || 3;
  match.config.roundSeconds = Number($<HTMLInputElement>('opt-seconds').value) || 120;
  match.config.mode = 'punch-out';
  match.setOpponent(personalityById(selectedPersonality), difficultyById(selectedDifficulty));
  match.recogniser.reset();
  match.startMatch(performance.now());
  $('ai-name').textContent = personalityById(selectedPersonality).name.toUpperCase();
  show(null);
}

match.bus.on('feedback', (f) => {
  renderer.pushFeedback(f);
  if (f.who === 'player' && f.outcome === 'miss' && f.detail) setCoach(f.detail);
});

match.bus.on('telegraph', () => {
  /* Reserved for an audio cue: a short click at wind-up start dramatically
     improves reaction training, because auditory latency is ~40 ms lower than
     visual. Deferred until the audio pass so it can be mixed properly. */
});

// Reaction time is the number this mode exists to improve, so it is surfaced
// the instant it is measured rather than only in the end-of-round report.
match.bus.on('dodge', (g) => {
  if (g.kind === 'block') return;
  renderer.pushFeedback({
    t: performance.now(),
    who: 'ai',
    outcome: 'slipped',
    quality: g.quality,
    label: g.perfect ? 'PERFECT' : 'DODGED',
    detail: `${Math.round(g.reactionMs)}ms`,
  });
});

match.bus.on('knockdown', ({ who }) => {
  setCoach(who === 'ai' ? 'DOWN! Stay on him.' : 'You got caught. Hands up, reset.', 2600);
});

match.bus.on('round-end', (res) => {
  mode = 'round';
  $('round-result-title').textContent = `End of round ${res.round}`;
  $('round-cards').textContent = `${res.cards.player} – ${res.cards.ai}`;
  $('round-stats').innerHTML = [
    ['Landed', `${res.landed}/${res.thrown}`],
    ['Accuracy', res.thrown ? `${Math.round((res.landed / res.thrown) * 100)}%` : '–'],
    ['Absorbed', `${res.absorbed}/${res.incoming}`],
    ['Defence', res.incoming ? `${Math.round((1 - res.absorbed / res.incoming) * 100)}%` : '–'],
    ['Best combo', res.bestCombo || '–'],
    ['Rating', `${res.playerScore} (${Scorer.grade(res.playerScore)})`],
  ]
    .map(([k, v]) => `<div><b>${v}</b><span>${k}</span></div>`)
    .join('');
  $('round-advice').innerHTML = res.topAdvice.map((a) => `<li>${a}</li>`).join('') || '<li>Nothing to fix. Keep going.</li>';
  show('round');
});

match.bus.on('match-end', ({ cards }) => {
  mode = 'final';
  const channels = match.scorer.channels();
  const overall = match.scorer.overall();
  const won = cards.player > cards.ai;
  $('final-verdict').innerHTML =
    `${cards.player} – ${cards.ai} · <span style="color:${won ? 'var(--accent)' : 'var(--danger)'}">${won ? 'You took it' : cards.player === cards.ai ? 'Draw' : 'They took it'}</span>` +
    `<div style="font-size:15px;color:var(--muted);margin-top:6px">Overall ${overall} · grade ${Scorer.grade(overall)}</div>`;

  $('final-channels').innerHTML = channels
    .map((c) => {
      const t = progress.trend(store, c.id);
      const delta = t === null ? '' : ` <span style="color:${t >= 0 ? 'var(--accent)' : 'var(--danger)'}">${t >= 0 ? '+' : ''}${t}</span>`;
      return `<div class="channel${c.evidence < 4 ? ' thin' : ''}">
        <div class="top"><span>${c.label}</span><b>${c.evidence < 4 ? '–' : c.rating}${delta}</b></div>
        <div class="meter"><i style="width:${c.rating}%"></i></div>
      </div>`;
    })
    .join('');
  $('final-advice').innerHTML = match.scorer.advice(3).map((a) => `<li>${a}</li>`).join('') || '<li>Solid across the board.</li>';

  const totals = match.results.reduce(
    (a, r) => ({ landed: a.landed + r.landed, thrown: a.thrown + r.thrown, absorbed: a.absorbed + r.absorbed }),
    { landed: 0, thrown: 0, absorbed: 0 },
  );
  store = progress.record(
    store,
    {
      opponent: selectedPersonality,
      difficulty: selectedDifficulty,
      rounds: match.results.length,
      overall,
      cards,
      ...totals,
    },
    channels,
    (performance.now() - sessionStart) / 60000,
  );

  show('final');
});

/* ---------------------------------------------------------------- coach line */

let coachUntil = 0;
function setCoach(text: string, ms = 1700): void {
  const el = $('coach');
  el.textContent = text;
  el.classList.add('show');
  coachUntil = performance.now() + ms;
}

/* ----------------------------------------------------------------- HUD sync */

let hudAccum = 0;
function updateHud(dtMs: number, now: number): void {
  hudAccum += dtMs;
  if (hudAccum < 100) return;
  hudAccum = 0;

  $('round-timer').textContent = match.phase === 'rest' ? fmtTime(match.restTimeMs) : fmtTime(match.roundTimeMs);
  $('round-label').textContent = match.phase === 'rest' ? 'REST' : `ROUND ${match.round}`;
  $('cards').textContent = `${match.cards.player} – ${match.cards.ai}`;
  $<HTMLElement>('p-poise').style.width = `${match.player.vitals.poise}%`;
  $<HTMLElement>('p-stam').style.width = `${match.player.vitals.stamina}%`;
  $<HTMLElement>('a-poise').style.width = `${match.ai.vitals.poise}%`;
  $<HTMLElement>('a-stam').style.width = `${match.ai.vitals.stamina}%`;

  const s = match.scorer;
  const rt = s.meanReactionMs();
  $('stat-reaction').textContent = rt > 0 ? `${Math.round(rt)}ms` : '–';
  $('stat-landed').textContent = String(s.landed);
  $('stat-def').textContent = s.incoming ? `${Math.round((1 - s.absorbed / s.incoming) * 100)}%` : '–';
  const ex = match.exchange;
  $('stat-punish').textContent = ex.windowsOpened ? `${ex.windowsUsed}/${ex.windowsOpened}` : '–';
  const read = Math.round(match.ai.blackboard.model.predictability * 100);
  $('stat-read').textContent = `${read}%`;

  if (read > 62 && now > coachUntil + 4000) setCoach('You are getting predictable — change the combination');

  if (now > coachUntil) $('coach').classList.remove('show');

  const perf = $('perf');
  if (!perf.hidden) {
    perf.textContent = [
      `fps    ${loop.stats.fps.toFixed(0)}`,
      `sim    ${loop.stats.simHz.toFixed(0)} Hz`,
      `frame  ${loop.stats.frameMs.toFixed(1)} ms`,
      `pose   ${(pose?.inferenceMs ?? 0).toFixed(1)} ms`,
      `track  ${Math.round((latestFrame?.quality ?? 0) * 100)}%`,
      `gap    ${match.gap.toFixed(2)} sw`,
      `ai     ${match.ai.state}`,
      `react  ${match.ai.difficulty.reactionMs.toFixed(0)} ms`,
      `adapt  ${match.adaptive.pressure >= 0 ? '+' : ''}${match.adaptive.pressure.toFixed(2)}`,
    ].join('\n');
  }
}

/* -------------------------------------------------------------- adaptive QoS */

/**
 * If the frame budget is blown we degrade in a fixed order, worst-looking-last:
 *   1. drop the tracking overlay (pure cosmetics)
 *   2. drop DPR to 1 (halves shaded pixels)
 * We never throttle the pose pipeline, because reducing input rate degrades the
 * thing the game is actually made of. Better an ugly 30 fps than a laggy 60.
 */
let degradeStep = 0;
let overBudgetMs = 0;
function checkPerf(dtMs: number): void {
  if (loop.stats.frameMs > 22) overBudgetMs += dtMs;
  else overBudgetMs = Math.max(0, overBudgetMs - dtMs * 0.5);
  if (overBudgetMs > 3000 && degradeStep === 0) {
    degradeStep = 1;
    renderer.showSkeleton = false;
    overBudgetMs = 0;
  } else if (overBudgetMs > 4000 && degradeStep === 1) {
    degradeStep = 2;
    Object.defineProperty(window, 'devicePixelRatio', { value: 1, configurable: true });
    renderer.resize();
    overBudgetMs = 0;
  }
}

/* ----------------------------------------------------------------- game loop */

const loop = new GameLoop({
  update(dt, now) {
    if (mode === 'fight') match.update(dt, now);
    else if (mode === 'round' && match.phase === 'rest') {
      match.update(dt, now);
      $('rest-countdown').textContent = `Next round in ${fmtTime(match.restTimeMs)}`;
      // `update` may have rolled the rest timer over into the next round.
      if ((match.phase as Phase) === 'fighting') {
        mode = 'fight';
        show(null);
      }
    }
  },
  render(_alpha, now) {
    const dt = 1000 / Math.max(30, loop.stats.fps || 60);
    renderer.render(match, latestFrame, now, dt);
    if (mode === 'fight' || mode === 'round') updateHud(dt, now);
    else if (mode === 'calibrate') tickCalibrationWatchdog(now);
    checkPerf(dt);
  },
});
loop.start();

/* ------------------------------------------------------------------ controls */

$('btn-start').onclick = () => void startCamera();

/**
 * The tuner needs a live camera and a calibration, so it reuses whatever the
 * session already has and starts them if it does not. Entering it from a cold
 * start runs the normal calibration first — thresholds measured against a bad
 * calibration would be worse than useless.
 */
$('btn-tuner').onclick = () => {
  void (async () => {
    if (!pose) {
      const saved = loadCalibration();
      await startCamera();
      if (!pose) return;
      if (saved) {
        processor.calibration = saved;
        openTuner();
      }
      // Otherwise calibration is running; the user can open the tuner after.
      return;
    }
    openTuner();
  })();
};

function openTuner(): void {
  if (!tuner) {
    tuner = new Tuner($('screen-tuner'), processor);
    tuner.onClose = () => {
      mode = 'menu';
      video.classList.remove('calibrating');
      show('menu');
      $('hud').hidden = true;
    };
    tuner.onRecalibrate = () => beginCalibration();
  }
  mode = 'tuner';
  show('tuner');
  $('hud').hidden = true;
}
$('btn-calib-skip').onclick = () => {
  const saved = loadCalibration();
  if (saved) {
    processor.calibration = saved;
    video.classList.remove('calibrating');
    showReady();
  }
};
$('btn-calib-force').onclick = () => {
  // Take whatever we can see right now, repeatedly, so the medians have
  // something to work with, then proceed. Imprecise beats stuck.
  for (let i = 0; i < 20 && latestRaw; i++) processor.forceSample(latestRaw);
  finishCalibration();
};
$('btn-calib-cancel').onclick = () => {
  mode = 'menu';
  video.classList.remove('calibrating');
  show('menu');
  $('hud').hidden = true;
  $<HTMLButtonElement>('btn-start').disabled = false;
};
$('btn-fight').onclick = () => startFight();
$('btn-recalib').onclick = () => beginCalibration();
$('btn-warmup').onclick = () => beginWarmup();
$('btn-warmup-skip').onclick = () => skipWarmup();
$('btn-continue').onclick = () => {
  if (match.phase === 'rest') match.nextRound(performance.now());
  mode = 'fight';
  show(null);
};
$('btn-again').onclick = () => startFight();
$('btn-menu').onclick = () => {
  mode = 'menu';
  show('menu');
  $('hud').hidden = true;
};
$<HTMLInputElement>('opt-skeleton').onchange = (e) => (renderer.showSkeleton = (e.target as HTMLInputElement).checked);
$<HTMLInputElement>('opt-perf').onchange = (e) => ($('perf').hidden = !(e.target as HTMLInputElement).checked);
$<HTMLInputElement>('opt-adaptive').onchange = (e) =>
  match.adaptive.setEnabled((e.target as HTMLInputElement).checked);

// Pausing on tab-hide is not a nicety: a webcam pipeline left running in a
// background tab is both a battery and a privacy problem.
document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    loop.stop();
    pose?.stop();
  } else {
    pose?.start();
    loop.start();
  }
});

window.addEventListener('beforeunload', () => pose?.dispose());

// Expose for console tuning during development.
Object.assign(window as unknown as Record<string, unknown>, { match, processor, renderer, clamp01 });
